import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { companyDetailQueryOptions } from '@/features/company/api/queries'
import {
  DEFAULT_FLOORSHEET_PERIOD,
  parseFloorsheetPeriod,
  type FloorsheetPeriod,
} from '@/features/company/lib/floorsheetPeriod'
import { watchlistCollectionQueryOptions } from '@/features/watchlist/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { prefetchStockTab } from '@/screens/stock/lib/tabReads'
import {
  BUILT_TABS,
  DEFAULT_ADVANCED_TAB,
  normaliseAdvancedTab,
  normaliseCompareWith,
  normaliseStockTab,
  type AdvancedTab,
  type StockTab,
} from '@/screens/stock/stockData'

/**
 * `?tab=` is parsed through `normaliseStockTab` rather than a bare `z.enum`
 * so V1's `analysis` still resolves — to `technical`, its V2 name. A plain
 * enum with `.catch('overview')` would not merely fail to route it, it would
 * quietly land a member on a DIFFERENT screen than the one they bookmarked.
 *
 * Here rather than in StockScreen.tsx because the loader below parses the URL
 * through it too, and the route's `validateSearch` and the loader must not be
 * able to disagree about which tab a URL means.
 *
 * The other five are what a tab keeps in the URL so Back, a reload and a
 * shared link land on the same view. Everything else a panel selects — a page
 * of a table, an expanded row, a chip — stays in the panel's own state.
 *
 * - `sub`: the Advanced tab's sub-tab. Anything the strip does not draw as a
 *   tab (unknown, or a Soon one such as an old `?sub=value`) opens SWOT, like
 *   `?tab=advanced` with none, so old links keep their view. It parses to
 *   `undefined` for SWOT rather than to `'swot'`: the router writes a route's
 *   PARSED search into the URL on every navigation, so a default that parsed
 *   to a value would add `sub=swot` to every tab the member opens next.
 *   Read it through `useStockSearch`, which puts the default back.
 * - `fy`, `q`: the filing period a figure is for, as mm-be spells it — its
 *   `?year=` (`082083`) and `?period=` (`q1`…`q4`, or `annual` on a panel). Absent
 *   means the company's latest filing. Shared by every tab that has a period
 *   picker (Financials and the Advanced panels), so a member who picks Q3 on
 *   Health is still on Q3 on Earnings. Declared here, untyped beyond "a string",
 *   so a panel can start using them without touching the route; what a value
 *   MEANS — and what to do with one the company has no filing for — is the
 *   panel's.
 * - `with`: the companies a comparison adds to the page's own — two at most.
 *   The Compare tab and Advanced › Peers read and write it, and leaving either
 *   drops it. Shaped like Brokers › Compare's `codes`: written as a real array,
 *   which the router JSON-encodes (`with=["GBIME","EBL"]`), read as that or as
 *   a hand-typed comma string (`with=GBIME,EBL`), and always parsed to an array
 *   of upper-cased, distinct symbols. None parses to `undefined`, for the
 *   reason `sub` does: an empty array that parsed to a value would be written
 *   onto every tab's URL. The page's own symbol, and the cap of two, are the
 *   screen's to apply (`cleanCompareWith`) — a `validateSearch` sees no path
 *   params, so this parse keeps one spare for it.
 * - `fp`: the Floorsheet tab's window, `yesterday` or `week`. Today, the
 *   default, parses to `undefined` for the reason `sub` does.
 *
 * `sub`, `fy`, `q`, `with` and `fp` fall to undefined rather than failing the parse:
 * TanStack reads `?q=4` as the number 4, and a bad link must not become the
 * route's error page.
 */
export const stockSearchSchema = z.object({
  tab: z
    .string()
    .optional()
    .transform((value): StockTab => normaliseStockTab(value)),
  sub: z
    .string()
    .optional()
    .catch(undefined)
    .transform((value): AdvancedTab | undefined => {
      const sub = normaliseAdvancedTab(value, BUILT_TABS)
      return sub === DEFAULT_ADVANCED_TAB ? undefined : sub
    }),
  fy: z.string().min(1).optional().catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
  with: z
    .union([z.array(z.string()), z.string().transform((raw) => raw.split(','))])
    .transform((symbols): string[] | undefined => {
      const picks = normaliseCompareWith(symbols)
      return picks.length === 0 ? undefined : picks
    })
    .optional()
    .catch(undefined),
  fp: z
    .string()
    .optional()
    .catch(undefined)
    .transform((value): FloorsheetPeriod | undefined => {
      const period = parseFloorsheetPeriod(value)
      return period === null || period === DEFAULT_FLOORSHEET_PERIOD ? undefined : period
    }),
})

/**
 * The `/stocks/$symbol` loader.
 *
 * MAIN: the company — the header and every tab are drawn from it — awaited.
 *
 * SECONDARY, started and not awaited: the tab the URL opens on (see
 * `prefetchStockTab`) and the member's watchlists, which only decide whether
 * the header's button reads "Watch" or "Watching".
 *
 * `?tab=` is read from `location.search` through the route's own schema, not
 * through `loaderDeps`: a dep would make every tab switch a new route match —
 * the full-page pending skeleton on each click. As it is, a tab switch runs
 * no loader at all and the panel reads for itself; see "Search params" in
 * app/router.tsx.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree. It imports from `api/` and `lib/`
 * modules only — route loaders are not code-split, so anything imported here
 * ships in the entry chunk.
 *
 * SECONDARY reads only run on a real navigation (`!preload`): the main data
 * still loads on hover (`ensureRouteData` below is unconditional), but a
 * cursor passing over a table of stock links must not fire each row's tab
 * read and a watchlist read for every symbol it crosses.
 */
export async function loadStock({
  context: { queryClient },
  params,
  location,
  preload,
}: {
  context: RouterContext
  params: { symbol: string }
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const symbol = params.symbol.toUpperCase()
  if (!preload) {
    prefetchStockTab(queryClient, symbol, stockSearchSchema.parse(location.search).tab)
    prefetchRouteData(queryClient, watchlistCollectionQueryOptions())
  }
  await ensureRouteData(queryClient, companyDetailQueryOptions(symbol))
}
